import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import { UserCheck, Plus, Pencil, Trash2 } from 'lucide-react'

import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { DataTable } from '@/components/table'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { useToast } from '@/hooks/use-toast'
import { affectationService, AffectationProfile } from '@/api/affectation'
import { ProfileDialog } from './components/profile-dialog'

function WildcardBadge({ label }: { label: string }) {
  return (
    <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
      {label}
    </Badge>
  )
}

function ItemBadges({ items }: { items: { id: number; name: string }[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {items.slice(0, 2).map((i) => (
        <Badge key={i.id} variant="secondary" className="text-xs font-normal">
          {i.name}
        </Badge>
      ))}
      {items.length > 2 && (
        <Badge variant="secondary" className="text-xs font-normal">
          +{items.length - 2}
        </Badge>
      )}
    </div>
  )
}

export default function AffectationPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [profileDialog, setProfileDialog] = useState<{ open: boolean; profile?: AffectationProfile }>({ open: false })
  const [deletingProfile, setDeletingProfile] = useState<AffectationProfile | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)

  const queryKey = ['affectation-profiles']

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => affectationService.listProfiles({ limit: 200 }),
  })

  const profiles = data?.results ?? []

  const handleToggle = async (profile: AffectationProfile) => {
    setTogglingId(profile.id)
    try {
      await affectationService.toggleProfile(profile.id)
      queryClient.invalidateQueries({ queryKey })
    } finally {
      setTogglingId(null)
    }
  }

  const handleDelete = async () => {
    if (!deletingProfile) return
    try {
      await affectationService.deleteProfile(deletingProfile.id)
      toast({ title: t('aff_profile_deleted') })
      queryClient.invalidateQueries({ queryKey })
    } finally {
      setDeletingProfile(null)
    }
  }

  const columns = useMemo<ColumnDef<AffectationProfile>[]>(
    () => [
      {
        id: 'lawyer',
        header: t('aff_col_lawyer'),
        cell: ({ row }) => (
          <span className="font-medium text-sm">{row.original.user_name}</span>
        ),
      },
      {
        id: 'villes',
        header: t('aff_col_villes'),
        cell: ({ row }) =>
          row.original.villes.length === 0 ? (
            <WildcardBadge label={t('aff_any')} />
          ) : (
            <ItemBadges items={row.original.villes} />
          ),
      },
      {
        id: 'tribunal_types',
        header: t('aff_col_tribunal_types'),
        cell: ({ row }) =>
          row.original.tribunal_types.length === 0 ? (
            <WildcardBadge label={t('aff_any')} />
          ) : (
            <ItemBadges items={row.original.tribunal_types} />
          ),
      },
      {
        id: 'case_types',
        header: t('aff_col_case_types'),
        cell: ({ row }) =>
          row.original.case_types.length === 0 ? (
            <WildcardBadge label={t('aff_any')} />
          ) : (
            <ItemBadges items={row.original.case_types} />
          ),
      },
      {
        id: 'status',
        header: t('aff_col_status'),
        cell: ({ row }) => (
          <Switch
            checked={row.original.is_active}
            disabled={togglingId === row.original.id}
            onCheckedChange={() => handleToggle(row.original)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center gap-1 justify-end">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation()
                setProfileDialog({ open: true, profile: row.original })
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation()
                setDeletingProfile(row.original)
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ),
      },
    ],
    [t, togglingId]
  )

  return (
    <Main>
      <PageHeader
        leading={
          <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <UserCheck className="h-5 w-5" />
          </span>
        }
        title={t('affectation')}
        description={t('affectation_desc')}
        actions={
          <Button onClick={() => setProfileDialog({ open: true })}>
            <Plus className="me-2 h-4 w-4" />
            {t('aff_add_profile')}
          </Button>
        }
      />

      <div className="-mx-4 flex-1 overflow-auto px-4 py-1">
        {isLoading ? (
          <DataTableSkeleton columns={6} rows={8} />
        ) : profiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-dashed border-slate-300 dark:border-slate-700">
            <UserCheck className="h-10 w-10 mb-2 opacity-20" />
            <p className="text-sm">{t('aff_no_profiles')}</p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={profiles}
            pages={1}
            params={{ page: 0, page_size: profiles.length }}
            setParams={() => {}}
            showPagination={false}
          />
        )}
      </div>

      <ProfileDialog
        open={profileDialog.open}
        profile={profileDialog.profile}
        onClose={() => setProfileDialog({ open: false })}
        onSaved={() => queryClient.invalidateQueries({ queryKey })}
      />

      <ConfirmDialog
        open={!!deletingProfile}
        onOpenChange={(v) => !v && setDeletingProfile(null)}
        title={t('aff_delete_profile')}
        desc={t('aff_delete_profile_desc')}
        destructive
        handleConfirm={handleDelete}
      />
    </Main>
  )
}
